export class SiteHeader extends HTMLElement {
    private static readonly FOCUSABLE_SELECTORS = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';
    private static readonly MOBILE_BREAKPOINT = 992;
    
    private currentPanel: HTMLElement | null = null;
    private lastFocusedElement: HTMLElement | null = null;
    private escapeHandler: (e: KeyboardEvent) => void;
    private resizeHandler: () => void;
    private outsideClickHandler: (e: MouseEvent) => void;
    private focusHandler: (e: FocusEvent) => void;
    private panelKeyHandler: ((e: KeyboardEvent) => void) | null = null;

    constructor() {

        console.log('site header');

        super();
        this.escapeHandler = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && this.currentPanel) {
                const focusTarget = this.lastFocusedElement;
                this.closeAllPanels();
                if (focusTarget) {
                    focusTarget.focus();
                }
            }
        };
        this.resizeHandler = () => {
            if (window.innerWidth >= SiteHeader.MOBILE_BREAKPOINT) {
                document.body.style.overflow = '';
            }
        };
        this.outsideClickHandler = (e: MouseEvent) => {
            if (!this.currentPanel) return;
            
            const target = e.target as HTMLElement;
            // Ne pas fermer si on clique dans le panel ou sur un lien avec data-target-panel
            if (!this.currentPanel.contains(target) && !target.closest('[data-target-panel]')) {
                this.closeAllPanels();
            }
        };
        this.focusHandler = (e: FocusEvent) => {
            if (!this.currentPanel) return;
            
            const target = e.target as HTMLElement;
            // Si le focus quitte le panel et n'est pas dans un autre panel, fermer le panel actuel
            if (!this.currentPanel.contains(target) && !target.closest('.nav-panel')) {
                this.closeAllPanels();
            }
        };
    }

    connectedCallback() {
        this.init();
    }

    disconnectedCallback() {
        document.removeEventListener('keydown', this.escapeHandler);
        document.removeEventListener('click', this.outsideClickHandler);
        document.removeEventListener('focusin', this.focusHandler);
        window.removeEventListener('resize', this.resizeHandler);
    }

    private init() {
        this.setupPanelLinks();
        this.setupCloseButtons();
        this.setupMobileNav();
        this.initializePanels();
        
        // Gestion de la touche Échap
        document.addEventListener('keydown', this.escapeHandler);
        
        // Gestion du resize
        window.addEventListener('resize', this.resizeHandler);
    }

    private setupPanelLinks() {
        const navLinks = this.querySelectorAll('[data-target-panel]');
        navLinks.forEach(link => {
            const targetSelector = link.getAttribute('data-target-panel');
            if (targetSelector) {
                const panel = this.querySelector(targetSelector);
                if (panel) {
                    link.classList.add('has-panel');
                    link.addEventListener('click', (e) => {
                        e.preventDefault();
                        this.togglePanel(link as HTMLAnchorElement, panel as HTMLElement);
                    });
                }
            }
        });
    }

    private setupCloseButtons() {
        // Délégation d'événements
        this.addEventListener('click', (e) => {
            const target = e.target as HTMLElement;
            if (target.closest('[data-close-panel]')) {
                this.closeAllPanels();
            }
        });
    }

    private setupMobileNav() {
        const navToggle = this.querySelector('[data-toggle-nav]');
        const mainNav = this.querySelector('.main-nav');
        
        if (navToggle && mainNav) {
            navToggle.addEventListener('click', () => {
                const isOpened = mainNav.classList.toggle('opened');
                navToggle.classList.toggle('active', isOpened);
                
                // Locker/délocker le scroll en mobile
                if (window.innerWidth < SiteHeader.MOBILE_BREAKPOINT) {
                    document.body.style.overflow = isOpened ? 'hidden' : '';
                }
                
                if (!isOpened) {
                    this.closeAllPanels();
                }
            });
        }
    }

    private initializePanels() {
        const panels = this.querySelectorAll('.nav-panel');
        panels.forEach(panel => {
            this.disableFocusInPanel(panel as HTMLElement);
        });
    }

    private togglePanel(link: HTMLAnchorElement, panel: HTMLElement) {
        const isAlreadyOpen = panel.classList.contains('opened');

        // Fermer tous les panels et retirer toutes les classes active
        this.closeAllPanels();

        // Si le panel n'était pas déjà ouvert, l'ouvrir
        if (!isAlreadyOpen) {
            this.lastFocusedElement = link;
            panel.classList.add('opened');
            link.classList.add('active');
            this.currentPanel = panel;
            this.enableFocusInPanel(panel);
            this.setupPanelKeyboardNavigation(link, panel);
            
            // Ajouter le listener pour fermer au clic en dehors
            setTimeout(() => {
                document.addEventListener('click', this.outsideClickHandler);
                document.addEventListener('focusin', this.focusHandler);
            }, 0);
        }
    }

    private closeAllPanels() {
        // Retirer le listener de navigation clavier si présent
        if (this.panelKeyHandler) {
            document.removeEventListener('keydown', this.panelKeyHandler, true);
            this.panelKeyHandler = null;
        }
        
        // Fermer tous les panels
        const panels = this.querySelectorAll('.nav-panel');
        panels.forEach(panel => {
            panel.classList.remove('opened');
            this.disableFocusInPanel(panel as HTMLElement);
        });

        // Retirer toutes les classes active des éléments avec data-target-panel
        const activeElements = this.querySelectorAll('[data-target-panel].active');
        activeElements.forEach(element => {
            element.classList.remove('active');
        });
        
        // Retirer les listeners
        document.removeEventListener('click', this.outsideClickHandler);
        document.removeEventListener('focusin', this.focusHandler);
        
        this.currentPanel = null;
    }

    private disableFocusInPanel(panel: HTMLElement) {
        const elements = panel.querySelectorAll(SiteHeader.FOCUSABLE_SELECTORS);
        elements.forEach((el: Element) => {
            (el as HTMLElement).setAttribute('tabindex', '-1');
        });
    }

    private enableFocusInPanel(panel: HTMLElement) {
        const elements = panel.querySelectorAll('[tabindex="-1"]');
        elements.forEach((el: Element) => {
            (el as HTMLElement).removeAttribute('tabindex');
        });
    }

    private setupPanelKeyboardNavigation(link: HTMLAnchorElement, panel: HTMLElement) {
        const mainNav = this.querySelector('.main-nav ul');
        if (!mainNav) return;

        const mainNavLinks = Array.from(mainNav.querySelectorAll('a'));
        const currentLinkIndex = mainNavLinks.indexOf(link);
        
        this.panelKeyHandler = (e: KeyboardEvent) => {
            if (e.key !== 'Tab') return;
            
            const target = e.target as HTMLElement;
            const focusableElements = Array.from(
                panel.querySelectorAll<HTMLElement>(SiteHeader.FOCUSABLE_SELECTORS)
            ).filter(el => {
                const style = window.getComputedStyle(el);
                return style.display !== 'none' && style.visibility !== 'hidden';
            });

            // Si on est sur le lien de navigation et qu'on appuie sur Tab (sans Shift)
            if (target === link && !e.shiftKey && focusableElements.length > 0) {
                e.preventDefault();
                focusableElements[0].focus();
                return;
            }

            // Si on est sur le dernier élément du panel et qu'on appuie sur Tab (sans Shift)
            if (!e.shiftKey && focusableElements.length > 0) {
                const lastElement = focusableElements[focusableElements.length - 1];
                if (target === lastElement) {
                    e.preventDefault();
                    // Aller au prochain lien de navigation ou revenir au premier
                    const nextLink = mainNavLinks[currentLinkIndex + 1] || mainNavLinks[0];
                    if (nextLink) {
                        nextLink.focus();
                    }
                    return;
                }
            }

            // Si on est sur le premier élément du panel et qu'on appuie sur Shift+Tab
            if (e.shiftKey && focusableElements.length > 0) {
                const firstElement = focusableElements[0];
                if (target === firstElement) {
                    e.preventDefault();
                    link.focus();
                    return;
                }
            }
        };

        document.addEventListener('keydown', this.panelKeyHandler, true);
    }
}
